import React, { useCallback, useState } from 'react';
import ReactQuill from 'react-quill-new';
import styled from 'styled-components';
import { Tooltip } from './Tooltip';

const StyledReactQuill = styled(ReactQuill)`
  background-color: ${({ theme }) => theme.colors.white};
`;

const CustomLabel = styled.div`
  margin-top: 1rem;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1rem;
  color: #333;
`;

type QuillModules = 'toolbar' | 'keyboard' | 'history' | 'clipboard' | 'syntax';

type WysiwygEditorProps = {
  allowAllFormats?: boolean; // when passed `formats` prop will be ignored
  formats?: string[];
  initialValue?: string;
  isRequired?: boolean;
  label: string;
  modules?: Partial<Record<QuillModules, unknown>>;
  onChange: (value: string) => void;
  toolTipText?: string;
};

// defaults are what the mobile app is able to display
const defaultModules = {
  // Controls that are visible in the toolbar.
  // Controls are grouped into arrays so there is a bit more padding between groups
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold'],
    [{ list: 'bullet' }],
    ['link', 'image'],
    ['clean'],
  ],
};
// Allowed formats, e.g. even if "bold" is not in the toolbar, a user can paste a bold text,
// and its format will be respected
const defaultFormats = ['header', 'bold', 'list', 'link', 'image'];

/*


Our current WYSIWYG editor is Quill via react-quill-new
https://quilljs.com/docs/api/
https://github.com/VaguelySerious/react-quill

react-quill-new does not use a form field, so requires special handling to
work with react-final-form.


*/

const QUILL_EMPTY_VALUES = [
  '<p></p>',
  '<p><br/></p>',
  '<p><br /></p>',
  '<p><br></p>',
];

export const isWysiwygEditorEmpty = (value: string) =>
  !value || value.trim() === '' || QUILL_EMPTY_VALUES.includes(value.trim());

export function WysiwygEditor({
  allowAllFormats,
  formats = defaultFormats,
  initialValue = '',
  isRequired = false,
  label,
  modules = defaultModules,
  onChange,
  toolTipText,
}: WysiwygEditorProps) {
  const [value, setValue] = useState(initialValue);
  const handleChange = useCallback(
    (content, delta, source, editor) => {
      // Get clean, standard semantic HTML tags.
      // For example, Quill 2 uses custom utility attributes like <ol data-list="bullet"> instead of <ul> tag,
      // getSemanticHTML() converts those editor-specific nodes into clean, universal tags (like <ul> and <li>) that
      // can be rendered as plain html in web or in pdfs.
      // @see https://github.com/slab/quill/issues/3957#issuecomment-1895961689
      const semanticHTML = editor.getSemanticHTML();
      setValue(content);
      onChange(semanticHTML);
    },
    [onChange],
  );

  const required = isRequired ? '*' : '';

  const toolTip = toolTipText ? <Tooltip title={toolTipText} /> : '';

  return (
    <div>
      <CustomLabel>
        {label} {required} {toolTip}
      </CustomLabel>

      <StyledReactQuill
        formats={allowAllFormats ? undefined : formats}
        modules={modules}
        onChange={handleChange}
        value={value}
      />
    </div>
  );
}
